import React from 'react';
import { useGameStore } from '../../store/game-store.js';
import { getAutosave, loadData } from '../../lib/storage.js';
import { MODES } from '../../lib/constants.js';
import './Home.css';

export default function Home() {
  const setView = useGameStore(s => s.setView);
  const setGameMode = useGameStore(s => s.setGameMode);
  const updateGameOptions = useGameStore(s => s.updateGameOptions);
  const setTargets = useGameStore(s => s.setTargets);

  const autosave = getAutosave();
  const data = loadData();

  const handleModeSelect = (mode) => {
    setGameMode(mode);

    if (mode === MODES.EXPLORATION) {
      updateGameOptions({ isExploration: true, scoring: 'standard' });
    } else if (mode === MODES.CHAIN_REACTION) {
      updateGameOptions({ isExploration: false, scoring: 'chain-reaction' });
    } else if (mode === MODES.TIME_ATTACK) {
      updateGameOptions({ isExploration: false, scoring: 'standard', timeLimit: 120 });
    } else {
      updateGameOptions({ isExploration: false, scoring: 'standard', timeLimit: 0 });
    }

    setView('builder');
  };

  const handleQuickPlay = () => {
    setGameMode(MODES.CUSTOM);
    updateGameOptions({ isExploration: false, scoring: 'standard', timeLimit: 0 });
    setTargets([]);
    setView('builder');
    // Builder will auto-shuffle targets and start
  };

  return (
    <div className="home">
      <header className="home__header">
        <h1 className="home__title">Connect the Stars</h1>
        <p className="home__subtitle">Find the path between actors through the movies they share</p>
      </header>

      {autosave && (
        <button
          className="home__resume"
          onClick={() => {
            setTargets(autosave.targets || []);
            setView('builder');
          }}
        >
          <span className="home__resume-label">Resume Game</span>
          <span className="home__resume-detail">
            {autosave.targets?.length || 0} targets
            {autosave.won ? ' (won)' : ''}
          </span>
        </button>
      )}

      <div className="home__grid">
        <button className="home__card home__card--daily" onClick={() => handleModeSelect(MODES.DAILY)}>
          <div className="home__card-icon">&#x2B50;</div>
          <h2 className="home__card-title">Daily Challenge</h2>
          <p className="home__card-desc">Same puzzle for everyone. New at midnight UTC.</p>
          {data.daily.streak > 0 && (
            <span className="home__card-badge">{data.daily.streak} day streak</span>
          )}
        </button>

        <button className="home__card home__card--quick" onClick={handleQuickPlay}>
          <div className="home__card-icon">&#x26A1;</div>
          <h2 className="home__card-title">Quick Play</h2>
          <p className="home__card-desc">Random targets, default settings. Jump right in.</p>
        </button>

        <button className="home__card home__card--custom" onClick={() => handleModeSelect(MODES.CUSTOM)}>
          <div className="home__card-icon">&#x1F3AC;</div>
          <h2 className="home__card-title">Custom Game</h2>
          <p className="home__card-desc">Pick your targets, choose your rules.</p>
        </button>

        <button className="home__card home__card--chain" onClick={() => handleModeSelect(MODES.CHAIN_REACTION)}>
          <div className="home__card-icon">&#x1F4A5;</div>
          <h2 className="home__card-title">Chain Reaction</h2>
          <p className="home__card-desc">Build the longest path. Avoid shortcuts.</p>
        </button>

        <button className="home__card home__card--explore" onClick={() => handleModeSelect(MODES.EXPLORATION)}>
          <div className="home__card-icon">&#x1F50D;</div>
          <h2 className="home__card-title">Explore</h2>
          <p className="home__card-desc">Browse freely. Add anything to the board.</p>
        </button>

        <button className="home__card home__card--time" onClick={() => handleModeSelect(MODES.TIME_ATTACK)}>
          <div className="home__card-icon">&#x23F1;</div>
          <h2 className="home__card-title">Time Attack</h2>
          <p className="home__card-desc">Race the clock. 120 seconds per round.</p>
        </button>

        <button className="home__card home__card--successive" onClick={() => handleModeSelect(MODES.SUCCESSIVE_ROUNDS)}>
          <div className="home__card-icon">&#x1F3C6;</div>
          <h2 className="home__card-title">Successive Rounds</h2>
          <p className="home__card-desc">5 rounds. Best overall score wins.</p>
        </button>

        <button className="home__card home__card--stats" onClick={() => setView('stats')}>
          <div className="home__card-icon">&#x1F4CA;</div>
          <h2 className="home__card-title">Stats</h2>
          <p className="home__card-desc">Your collection, affinity graph, and history.</p>
        </button>
      </div>

      {data.recentGames.length > 0 && (
        <div className="home__recent">
          <h3 className="home__recent-title">Recent Games</h3>
          <div className="home__recent-list">
            {data.recentGames.slice(0, 5).map((game, i) => (
              <div key={i} className="home__recent-item">
                <span className="home__recent-result">
                  {game.won ? `${game.score} films` : 'Incomplete'}
                </span>
                <span className="home__recent-date">
                  {new Date(game.date).toLocaleDateString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <footer className="home__footer">
        <p>Film data from TMDB. No accounts. All data stays on your device.</p>
      </footer>
    </div>
  );
}
